import React, { useState } from 'react';
import { useSnackbar } from 'notistack';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Grid from '@material-ui/core/Grid';
import Page from '@afosto/app-components-react/Page';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import ContentDrawer from '@afosto/app-components-react/ContentDrawer';
import AccessInviteForm from '../../components/AccessInviteForm';
import RequestAccessForm from '../../components/RequestAccessForm';
import ApprovedAuthorizations from './ApprovedAuthorizations';
import MyPendingInvites from './MyPendingInvites';
import MyPendingRequests from './MyPendingRequests';
import PendingAuthorizations from './PendingAuthorizations';
import { subtenants as subtenantsApi } from '@afosto/iam/api/Subtenants';
import PendingRequests from './PendingRequests';
import translations from './translations';

const ExternalAccess = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:users:read']);

  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [showCreateRequest, setShowCreateRequest] = useState(false);

  const handleCreateInvite = async ({ email, roles }) => {
    try {
      await subtenantsApi.sendInvite({
        email,
        roles,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.inviteSent), {
        variant: 'success',
      });
      setShowCreateInvite(false);
    } catch (error) {
      if (error?.response?.status === 404) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.userNotFound), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(intl.formatMessage(translations.feedback.inviteNotSent), {
          variant: 'error',
        });
      }
    }
  };

  const handleRequestAccess = async ({ signee, roles }) => {
    try {
      await subtenantsApi.sendSubtenantRequest({
        signee,
        roles,
      });

      enqueueSnackbar(intl.formatMessage(translations.feedback.accessRequested), {
        variant: 'success',
      });
      setShowCreateRequest(false);
    } catch (error) {
      if (error?.response?.status === 404) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.userNotFound), {
          variant: 'error',
        });
      } else if (error?.response?.status === 409) {
        enqueueSnackbar(intl.formatMessage(translations.feedback.accessAlreadyRequested), {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(intl.formatMessage(translations.feedback.accessNotRequested), {
          variant: 'error',
        });
      }
    }
  };

  let primaryAction = null;

  if (location.pathname === '/external-access') {
    primaryAction = {
      text: intl.formatMessage(translations.externalAccess.invite.button),
      action: () => {
        setShowCreateInvite(true);
      },
    };
  }

  if (location.pathname === '/external-access/my-requests') {
    primaryAction = {
      text: intl.formatMessage(translations.externalAccess.request.button),
      action: () => {
        setShowCreateRequest(true);
      },
    };
  }

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Grid container wrap="nowrap">
      <Page
        title={intl.formatMessage(translations.title)}
        style={{ flex: 1 }}
        fullWidth
        primaryAction={primaryAction}
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.approved),
              path: '/external-access',
            },
            {
              label: intl.formatMessage(translations.subNavigation.requested),
              path: '/external-access/requests',
            },
            {
              label: intl.formatMessage(translations.subNavigation.invited),
              path: '/external-access/invites',
            },
            {
              label: intl.formatMessage(translations.subNavigation.myInvites),
              path: '/external-access/my-invites',
            },
            {
              label: intl.formatMessage(translations.subNavigation.myRequests),
              path: '/external-access/my-requests',
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
      >
        <Switch>
          <Route path="/external-access/my-requests" component={MyPendingRequests} />
          <Route path="/external-access/requests" component={PendingRequests} />
          <Route path="/external-access/my-invites" component={MyPendingInvites} />
          <Route path="/external-access/invites" component={PendingAuthorizations} />
          <Route path="/external-access" component={ApprovedAuthorizations} />
        </Switch>
      </Page>
      <ContentDrawer
        title={intl.formatMessage(translations.externalAccess.invite.title)}
        isOpen={showCreateInvite}
        handleClose={() => setShowCreateInvite(false)}
        drawerWidth="400px"
      >
        <AccessInviteForm
          onSubmit={handleCreateInvite}
          onCancel={() => setShowCreateInvite(false)}
        />
      </ContentDrawer>
      <ContentDrawer
        title={intl.formatMessage(translations.externalAccess.request.title)}
        isOpen={showCreateRequest}
        handleClose={() => setShowCreateRequest(false)}
        drawerWidth="400px"
      >
        <RequestAccessForm
          onSubmit={handleRequestAccess}
          onCancel={() => setShowCreateRequest(false)}
        />
      </ContentDrawer>
    </Grid>
  );
};

export default ExternalAccess;
